import { Timestamp } from '../../shared/common.types';
import { PropertyItem } from '../components/device-overview-card/device-overview-card.models';
import { DeviceProperty, ResolvedSection } from '../models/DeviceDetail';
import { UiProfile, UiProfileProperty } from '../models/UiProfile';

import { enrichUiProperty } from './enrich-ui-property';
import buildViewer from './viewer-builder/buildViewer';

const RADIO_RANGE_LABEL = 'DISCRETE_RSSI_LEVEL';

const EXCLUDED_OVERVIEW_LABELS = ['EQUIPMENT_AVAILABLE'];

interface OverviewPropertiesResult {
    radioRangeValue: string;
    properties: PropertyItem[];
}

function isOverviewProperty(uiProfile: UiProfile, propertyId: string): boolean {
    const label = uiProfile.properties[propertyId]?.label;
    return label != null && !EXCLUDED_OVERVIEW_LABELS.includes(label);
}

function overrideDateAsShortDate(uiProperty: UiProfileProperty | undefined): UiProfileProperty | undefined {
    if (uiProperty?.formatter?.type === 'date') {
        return { ...uiProperty, formatter: { type: 'shortDate' as const } };
    }
    return uiProperty;
}

export function buildOverviewProperty(
    propertyId: string,
    uiProfile: UiProfile,
    properties: Record<string, DeviceProperty>,
    lastUpdateTime: Timestamp | undefined,
): PropertyItem {
    const rawUiProperty = uiProfile.properties[propertyId];
    const deviceProperty = properties[propertyId];
    const enriched = enrichUiProperty(rawUiProperty, deviceProperty?.metadata);
    const uiProperty = overrideDateAsShortDate(enriched);
    if (!uiProperty) {
        return { id: propertyId, label: propertyId, value: '—' };
    }
    const viewer = buildViewer(uiProperty, deviceProperty, { timestamp: lastUpdateTime });

    return {
        id: propertyId,
        label: enriched?.label ?? propertyId,
        value: viewer.type === 'text' ? viewer.value : '—',
    };
}

export function buildOverviewProperties(
    sections: ResolvedSection[],
    uiProfile: UiProfile,
    properties: Record<string, DeviceProperty>,
    lastUpdateTime: Timestamp | undefined,
): OverviewPropertiesResult {
    const allPropertyIds = sections
        .flatMap((section) => section.propertyIds)
        .filter((propertyId) => isOverviewProperty(uiProfile, propertyId));

    const allItems = allPropertyIds.map((id) => buildOverviewProperty(id, uiProfile, properties, lastUpdateTime));

    const radioItem = allItems.find((item) => item.label === RADIO_RANGE_LABEL);

    return {
        radioRangeValue: radioItem?.value ?? '',
        properties: allItems.filter((item) => item.label !== RADIO_RANGE_LABEL),
    };
}
